import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useApp } from "../context/AppContext";
import { SHAPES, DOOR_SHAPES, SHAPE_BY_ID, shapePathFor, isDoorShape, OPTIONAL_KEYS } from "../lib/windowShapes";
import { normalizeSpokenMeasure } from "../lib/spokenNumbers";
import { VoiceInputButton } from "./VoiceInputButton";

export function ShapeIcon({ shape, className = "h-8 w-8", side, dims }) {
  const sh = SHAPE_BY_ID[shape] || SHAPES[0];
  const flip = side === "R" && (shape === "quarter_circle" || shape === "right_triangle");
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <path d={dims ? shapePathFor(sh.id, dims) : sh.path} fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="6" strokeLinejoin="round"
        transform={flip ? "translate(100 0) scale(-1 1)" : sh.ovalIcon ? "translate(50 50) scale(0.7 1) translate(-50 -50)" : undefined} />
    </svg>
  );
}

const PLACEHOLDER = { w: '35 1/2"', h: '79 3/4"', leg: '12"', top: '24"', legL: '36"', legR: '60"', d: '24"', sl: '12"', slL: '12"', slR: '12"', tr: '14"', trL: '14"', trR: '14"', trS: '14"' };

/**
 * Specialty-shape picker + the measurements that shape needs.
 * value = { shape, dims: { w, h, leg, top, legL, legR, d, side } }
 */
export function ShapeDimsEditor({ shape, dims, onShape, onDims, hideShapes = false, kind = "window" }) {
  const { t } = useApp();
  const list = kind === "door" ? DOOR_SHAPES : SHAPES;
  const sh = SHAPE_BY_ID[shape] || list[0];
  const set = (k, v) => {
    const next = { ...dims, [k]: v };
    // half circle: height follows width unless the user typed it
    if (sh.auto && !dims._hTouched) Object.entries(sh.auto).forEach(([ak, fn]) => { if (ak !== k) next[ak] = fn(next); });
    if (k === "h") next._hTouched = true;
    onDims(next);
  };
  const fields = sh.fields.filter((k) => k !== "side");
  return (
    <div className="space-y-2 min-w-0 max-w-full" data-testid="shape-dims-editor">
      {!hideShapes && <Label className="text-xs">{t(kind === "door" ? "measure_door_config" : "measure_shape")}</Label>}
      {!hideShapes && <div className="flex gap-1.5 overflow-x-auto pb-1 w-full min-w-0 max-w-full" data-testid="shape-picker">
        {list.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onShape(s.id)}
            title={t(`shape_${s.id}`)}
            data-testid={`shape-${s.id}`}
            className={`shrink-0 flex flex-col items-center gap-0.5 rounded-lg border px-1.5 py-1 w-16 transition-colors ${shape === s.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}
          >
            <ShapeIcon shape={s.id} className="h-7 w-7" dims={isDoorShape(s.id) ? dims : undefined} />
            <span className="text-[9px] leading-tight text-center line-clamp-2">{t(`shape_${s.id}`)}</span>
          </button>
        ))}
      </div>}
      {sh.fields.includes("side") && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t("measure_dim_side")}</span>
          <div className="flex rounded-lg border overflow-hidden text-xs">
            {["L", "R"].map((sd) => (
              <button key={sd} type="button" onClick={() => onDims({ ...dims, side: sd })} data-testid={`shape-side-${sd}`}
                className={`px-3 h-8 flex items-center gap-1 ${(dims.side || "L") === sd ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                <ShapeIcon shape={shape} side={sd} className="h-4 w-4" /> {t(sd === "L" ? "side_left" : "side_right")}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* dictate width AND height at once: "44 3/4 por 21 1/2" fills both fields */}
      <div className="flex items-center gap-2" data-testid="measure-dictate-row">
        <VoiceInputButton className="shrink-0" onTranscript={(txt) => {
          const parts = normalizeSpokenMeasure(txt).split(/\s+x\s+/i).map((p) => p.trim()).filter(Boolean);
          if (!parts.length) return;
          const next = { ...dims };
          if (fields.includes("d")) next.d = parts[0];
          else { const [a, b] = parts; if (a) next.w = a; if (b) next.h = b; }
          onDims(next);
        }} />
        <span className="text-[11px] text-muted-foreground">{t("measure_dictate_hint")}</span>
      </div>
      <div className={`grid gap-2 ${fields.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
        {fields.map((k) => (
          <div key={k}>
            <Label className="text-xs">
              {isDoorShape(shape) && shape !== "door" && (k === "w" || k === "h") ? t(`measure_dim_${k}_total`) : t(`measure_dim_${k}`)}
              {OPTIONAL_KEYS.has(k) ? ` (${t("optional")})` : ""}
            </Label>
            <Input
              data-testid={`measure-dim-${k}`}
              value={dims[k] || ""}
              onChange={(e) => set(k, e.target.value)}
              onBlur={(e) => { const v = normalizeSpokenMeasure(e.target.value); if (v !== e.target.value) set(k, v); }}
              placeholder={PLACEHOLDER[k]}
              className="mt-1 font-mono-plex"
              inputMode="text"
            />
          </div>
        ))}
      </div>
      {/* live outline: the curve / peak angle follows the typed leg ÷ height ratio */}
      {(["springline", "eyebrow", "pentagon", "trapezoid"].includes(shape) || (isDoorShape(shape) && shape !== "door")) && (
        <div className="flex items-center gap-2 text-primary" data-testid="shape-live-preview">
          <ShapeIcon shape={shape} dims={dims} side={dims.side} className="h-14 w-14" />
          <span className="text-[11px] text-muted-foreground">{t("measure_shape_live_hint")}</span>
        </div>
      )}
    </div>
  );
}
